/**
 * Order / order-line status badge (DNKADM-0442). Label, tone and help text all come from
 * `@/domain/orderStatus`; an unknown status shows its raw value in a neutral chip.
 * Backend text is brand-scrubbed at this render boundary.
 */
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { orderStatusHelp, orderStatusLabel, orderStatusTone, type OrderStatus } from '@/domain/orderStatus';
import { scrubBrand } from '@/utils/whitelabel';

const COLOR = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  neutral: 'default',
} as const;

export function OrderStatusBadge({ status }: { status: OrderStatus | null | undefined }) {
  const label = scrubBrand(orderStatusLabel(status)) ?? 'Unknown';
  const help = orderStatusHelp(status);
  const chip = (
    <Chip size="small" label={label} color={COLOR[orderStatusTone(status)]} />
  );
  return help === null ? chip : <Tooltip title={help}>{chip}</Tooltip>;
}

/** The receipt "Status" row: label on the left, the badge on the right (+ help text when known). */
export function OrderStatusRow({ status }: { status: OrderStatus | null | undefined }) {
  const help = orderStatusHelp(status);
  return (
    <Stack sx={{ gap: 0.5 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Status</Typography>
        <OrderStatusBadge status={status} />
      </Stack>
      {help === null ? null : (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{help}</Typography>
      )}
    </Stack>
  );
}
